import React, { FC, useCallback } from 'react';
import { useDispatchRequest, useQuery } from '@redux-requests/react';
import { useHistory } from 'react-router';
import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';

import routesByName from 'constants/routes';
import { filterTypes } from 'constants/roleOptions';
import { createNewApp, ICountry, listPrivateApp } from '../store/actions';
import CreateAppFormContainer from './components/CreateAppFormContainer/CreateAppFormContainer';
import { AppsConstants } from '../store/constants';

const CreateAppForm: FC = () => {
  const dispatch = useDispatchRequest();
  const history = useHistory();
  const { data: countries } = useQuery({ type: AppsConstants.APP_COUNTRIES });

  const handleNewAppSubmit = useCallback(
    async (values: any) => {
      const {
        environments,
        roles,
        allowedCountries,
        isPublic,
        isOnlyBackEnd,
        ...otherValues
      } = values;
      const transformedValues = {
        ...otherValues,
        isOnlyBackEnd: isOnlyBackEnd === 'yes',
        isPublic: isPublic === 'yes',
        roles: filterTypes(roles)?.map(({ name, types }) => ({ name, types })),
        plan: otherValues.plan || 'CLASSIC', // plan must always be defined or app can't be installed
      };
      // fields only for public apps
      // if (isPublic === 'yes') { // THIS IS COMMENTED BECAUSE OF PAID APPS FOR PRIVATE APPS
      // map selected countries to static data country objects
      const allowedCountryObjects = countries.filter((countryObject: ICountry) =>
        allowedCountries.includes(countryObject.code)
      );
      // all country codes + duplicated
      const allowedCountryObjectLanguageCodes = allowedCountryObjects.reduce(
        (allLanguageCodes: string[], allowedCountryObject: ICountry) => {
          return [...allLanguageCodes, ...allowedCountryObject.languages];
        },
        []
      );
      // only unique country codes. Needed to prevent duplicated languages for countries with same language
      const uniqueAllowedCountryObjectLanguageCodes = allowedCountryObjectLanguageCodes.filter(
        (languageCode: string, index: number, self: string[]) => {
          return index === self.indexOf(languageCode);
        }
      );
      // app languages
      const appLanguages = uniqueAllowedCountryObjectLanguageCodes.map((languageCode: string) => ({
        language: languageCode,
      }));
      // add countries to payload
      transformedValues.allowedCountries = allowedCountries?.map((country: string) => ({
        country,
      }));
      // add languages to payload
      transformedValues.languages = appLanguages;
      // }
      const result = await dispatch(createNewApp(transformedValues));
      const { data = {}, error = {} } = result || {};

      if (data.id) {
        try {
          await dispatch(listPrivateApp(data.id, environments));
          history.push(routesByName.apps.detail.index(data.id));
        } catch (e) {
          console.error(e);
          return { [FORM_ERROR]: 'errors.somethingWentWrong' };
        }
      } else {
        const { data: errorsData = {} } = error || {};
        const { errors = [] } = errorsData || {};
        return { [FORM_ERROR]: errors[0] };
      }
    },
    [dispatch, history, countries]
  );

  return <Form onSubmit={handleNewAppSubmit} render={CreateAppFormContainer} />;
};

export default CreateAppForm;
